import { Stack, Typography } from '@mui/material';
import { useMemo } from 'react';

import { Callout } from '@/components/notifications/Callout/Callout';
import { useFamilyTreeContext } from '@/modules/familyTree/contexts';
import { useFamilyTreeRelationships } from '@/modules/familyTree/hooks/useFamilyTreeRelationships';
import {
  ADJACENT_RELATIONSHIP_TYPES,
  FamilyRelationshipType,
  RELATIONSHIP_TYPE_DISPLAY_NAMES,
  SidebarPerson,
} from '@/modules/familyTree/utils/relationshipUtils';
import { FamilyTreeWarningType } from '@/types/schema';

import { FamilyTreeRelationshipSection } from './FamilyTreeRelationshipSection';

export function LinkedPanel() {
  const { state, dispatch } = useFamilyTreeContext();

  const unlinedIndividualIds =
    state.familyTreeData?.warnings
      .filter(
        (warning) => warning.type === FamilyTreeWarningType.UnlinkedFamilyMember
      )
      .flatMap((warning) => warning.clientProfileIds) ?? [];

  const { selectedPerson, relationships } = useFamilyTreeRelationships();

  const isUnlinked = unlinedIndividualIds.includes(selectedPerson?.id ?? '');

  const skipRelationships = useMemo(() => {
    return (
      Object.entries(relationships) as [
        FamilyRelationshipType,
        SidebarPerson[],
      ][]
    ).flatMap(([type, relationships]) => {
      if (
        !ADJACENT_RELATIONSHIP_TYPES.includes(type) &&
        relationships.length > 0
      ) {
        return {
          type,
          relationships,
        };
      }
      return [];
    });
  }, [relationships]);

  const firstSkipRelationship = skipRelationships[0];
  const firstSkipRelationshipDisplayName =
    firstSkipRelationship?.relationships?.[0]?.displayName ?? '';

  const handleSelectPerson = (personId: string) => {
    dispatch({ type: 'OPEN_SIDE_PANEL', personId });
  };

  return (
    <Stack spacing={4}>
      {isUnlinked &&
        firstSkipRelationship &&
        firstSkipRelationshipDisplayName && (
          <Callout severity="success">
            <Stack spacing={0.5}>
              <Typography variant="title-xs">
                {`${firstSkipRelationshipDisplayName} identified as ${RELATIONSHIP_TYPE_DISPLAY_NAMES[firstSkipRelationship.type]} of ${selectedPerson?.displayName}`}
              </Typography>
              <Typography variant="body-xs">
                Add spouse/parent/child relationships to connect{' '}
                {selectedPerson?.displayName} to{' '}
                {firstSkipRelationshipDisplayName} in the diagram.
              </Typography>
            </Stack>
          </Callout>
        )}
      <FamilyTreeRelationshipSection
        variant="parents"
        relationships={relationships}
        onSelectPerson={handleSelectPerson}
      />
      <FamilyTreeRelationshipSection
        variant="spouse"
        relationships={relationships}
        onSelectPerson={handleSelectPerson}
      />
      <FamilyTreeRelationshipSection
        variant="children"
        relationships={relationships}
        onSelectPerson={handleSelectPerson}
      />
    </Stack>
  );
}
